import React, { useMemo } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';

import type { AIPlugin } from '../../../aiPluginType';
import { getMonitorWatchmenCommands } from '../../../pm-plugins/ai-monitor-watchmen/commands';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { createAnalyticsFlow } from '../../utils/analytics/analyticsFlow';
import type { AnalyticsFlow } from '../../utils/analytics/analyticsFlow';
import { AnalyticsFlowContextProvider } from '../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../utils/analytics/utils';

import { AnalyticsBridge } from './AnalyticsBridge';
import { useUIState } from './hooks/useUIState';
import type { Action, State } from './hooks/useUIState';
import { AcceptRejectSessionScreen } from './screens/AcceptReject';
import { DiscardSessionScreen } from './screens/Discard';
import { ErrorSessionScreen } from './screens/Error';
import { LoadingSessionScreen } from './screens/Loading';
import { NoImprovementSessionScreen } from './screens/NoImprovement';
import { PostStreamReviewScreen } from './screens/PostStreamReview';
import { PromptSessionScreen } from './screens/Prompt';

export interface SessionUIComponentProps {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: UiComponentFactoryParams['appearance'];
	providerFactory: ProviderFactory;
}

interface RenderScreenProps {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: UiComponentFactoryParams['appearance'];
	dispatch: React.Dispatch<Action>;
	providerFactory: ProviderFactory;
	uiState: State;
}

/**
 * RenderScreen is a separate component to prevent unnecessary unmount/remount cycles.
 * Defined outside SessionUIComponent to maintain a stable component reference.
 */
function RenderScreen({ api, appearance, dispatch, providerFactory, uiState }: RenderScreenProps) {
	switch (uiState) {
		case 'discard':
			return <DiscardSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'loading':
			return (
				<LoadingSessionScreen
					api={api}
					appearance={appearance}
					dispatch={dispatch}
					providerFactory={providerFactory}
				/>
			);

		case 'error':
			return <ErrorSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'prompt':
			return <PromptSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'acceptReject':
			return <AcceptRejectSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'noImprovement':
			return <NoImprovementSessionScreen api={api} appearance={appearance} dispatch={dispatch} />;

		// [CCI-15904] New AIFC Editor Experience — Post Stream Review floating toolbar.
		case 'postStreamReview':
			return <PostStreamReviewScreen api={api} appearance={appearance} dispatch={dispatch} />;

		case 'idle':
			return null;
	}
}

/**
 * SessionUIComponent renders the appropriate screen based on the current UI state.
 * It provides AnalyticsFlowContext to child components that need analytics tracking.
 */
export function SessionUIComponent({
	api,
	appearance,
	providerFactory,
}: SessionUIComponentProps): React.JSX.Element {
	const [uiState, dispatch] = useUIState(api);
	const { canInlineStreamChanges, isInDocumentStreamingEnabled, editorView } =
		useContentComponentContext();

	const { pluginState } = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => {
		const decorationState = state.aiExperienceState?.aiDecorationExperiencePluginState;
		return {
			pluginState: decorationState,
		};
	});

	const monitorWatchmenCommands = useMemo(
		() => getMonitorWatchmenCommands(editorView),
		[editorView],
	);

	// Read decoration state synchronously from editorView to ensure invoke
	// attributes are available on the first render. useSharedPluginStateWithSelector
	// may not have the state until a subsequent render, but useCreateAnalyticsFlow
	// captures invokeAttributes at creation time and never re-reads them.
	const decorationState = editorView?.state
		? aiExperienceDecorationPluginKey.getState(editorView.state)
		: undefined;

	const invokeAttributes = useMemo(
		() => ({
			invokedFrom: decorationState?.lastTriggeredFrom ?? pluginState?.lastTriggeredFrom,
			invokedFor: decorationState?.triggeredFor?.name ?? pluginState?.triggeredFor?.name ?? 'text',
			triggerMethod: decorationState?.triggerMethod ?? pluginState?.triggerMethod,
			experienceName: decorationState?.configItem?.key ?? pluginState?.configItem?.key,
			featureId: decorationState?.configItem?.featureId ?? pluginState?.configItem?.featureId,
			aiGroupId: decorationState?.currentSessionId ?? pluginState?.currentSessionId,
			aiSessionId: decorationState?.interactionSessionId ?? pluginState?.interactionSessionId ?? '',
			willAISuggestEmoji: decorationState?.willAISuggestEmoji ?? pluginState?.willAISuggestEmoji,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pluginState, decorationState],
	);

	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();

	const analyticsFlowRef = React.useRef<AnalyticsFlow>();
	const sessionIdRef = React.useRef<string | null>(null);
	if (
		!analyticsFlowRef.current ||
		(invokeAttributes.aiSessionId && sessionIdRef.current !== invokeAttributes.aiSessionId)
	) {
		sessionIdRef.current = invokeAttributes.aiSessionId;
		analyticsFlowRef.current = {
			...createAnalyticsFlow({
				fireAIAnalyticsEvent,
				invokeAttributes,
				canInlineStreamChanges,
				onCommonAttributesUpdated: monitorWatchmenCommands?.syncCommonAttributes,
				isInDocumentStreamingEnabled,
			}),
		};
	}

	return (
		<AnalyticsFlowContextProvider value={analyticsFlowRef.current}>
			<AnalyticsBridge api={api} uiState={uiState}>
				<RenderScreen
					api={api}
					appearance={appearance}
					dispatch={dispatch}
					providerFactory={providerFactory}
					uiState={uiState}
				/>
			</AnalyticsBridge>
		</AnalyticsFlowContextProvider>
	);
}
